import { useState, useEffect, useMemo } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';

// Single source of roster truth for the active workspace. Replaces the global
// userManager.getAllUsers() mock so a picker/list only ever shows the people of
// the workspace you're currently in:
//   business → GET /api/team/members
//   personal → you + GET /api/household members (family). Falls back to just you
//              when there are no household members.
//
// Every entry is normalised to { id, userId, name, email, avatar, color }.
// Household members have no user account, so their userId/email are null — they
// are KEPT here (not dropped); callers that require an account (e.g. @mentions,
// which need a userId to notify) filter on userId themselves.
//
// A module-level cache + in-flight dedupe means many hook instances mounted on
// one screen (e.g. one Avatar per assignee) share a SINGLE network call rather
// than each firing their own. The cache is keyed by workspace, in-memory only.

function normalizeTeamMember(m) {
  // /api/team/members returns the spread user (id === user id) + teamRole/memberId.
  return {
    id: m.id,
    userId: m.id,
    name: m.name || 'Unknown',
    email: m.email || null,
    avatar: m.avatar || null,
    color: m.color || null,
    department: m.department || null,   // UI-5: "Assign to" a department (already returned by /api/team/members)
  };
}

function normalizeHouseholdMember(m) {
  // /api/household returns householdMember rows: id is the householdMember id and
  // there is no exposed account id, so userId/email are null by design.
  return {
    id: m.id,
    userId: null,
    name: m.name || 'Unknown',
    email: null,
    avatar: null,
    color: null,
  };
}

function normalizeSelf(user) {
  return {
    id: user.id,
    userId: user.id,
    name: user.name || user.firstName || 'You',
    email: user.email || null,
    avatar: user.avatar || null,
    color: user.color || null,
  };
}

// ── Shared per-workspace cache ────────────────────────────────────────────────
// key `${wsType}:${activeWorkspaceId}` → resolved members array.
const rosterCache = new Map();
// key → in-flight Promise<members>, so concurrent consumers await one fetch.
const inflightRoster = new Map();
// mounted hook instances, notified so they re-fetch when the roster is invalidated.
const rosterSubscribers = new Set();

function rosterKey(wsType, workspaceId) {
  return `${wsType}:${workspaceId}`;
}

// Drops cached roster(s) and prompts every mounted consumer to re-fetch, so a
// team-member add/remove/role-change reflects in pickers and mentions without a
// full reload. Pass a workspaceId to clear just that workspace, or omit for all.
export function invalidateWorkspaceRoster(workspaceId) {
  const matches = (key) => (workspaceId ? key.endsWith(`:${workspaceId}`) : true);
  for (const key of [...rosterCache.keys()]) if (matches(key)) rosterCache.delete(key);
  for (const key of [...inflightRoster.keys()]) if (matches(key)) inflightRoster.delete(key);
  rosterSubscribers.forEach(fn => fn());
}

// Resolves the roster for a key, hitting the network at most once per key: a
// cache hit returns synchronously-resolved data; an in-flight request is shared;
// otherwise one fetch runs, populates the cache, and clears the in-flight entry.
function loadRoster(key, wsType, currentUser) {
  if (rosterCache.has(key)) return Promise.resolve(rosterCache.get(key));
  if (inflightRoster.has(key)) return inflightRoster.get(key);

  const promise = (async () => {
    let list = [];
    try {
      if (wsType === 'business') {
        const res = await api('/api/team/members');
        const data = res.ok ? await res.json() : { members: [] };
        list = (data.members || []).map(normalizeTeamMember);
      } else {
        // personal (also the "family" experience — no separate family type exists)
        const self = currentUser ? [normalizeSelf(currentUser)] : [];
        const res = await api('/api/household');
        const data = res.ok ? await res.json() : { members: [] };
        list = [...self, ...(data.members || []).map(normalizeHouseholdMember)];
      }
    } catch {
      list = wsType === 'business' ? [] : (currentUser ? [normalizeSelf(currentUser)] : []);
    }
    rosterCache.set(key, list);
    inflightRoster.delete(key);
    return list;
  })();

  inflightRoster.set(key, promise);
  return promise;
}

export function useWorkspaceRoster() {
  const { currentUser, workspaces, activeWorkspaceId } = useCurrentUser();

  const wsType = useMemo(() => {
    const ws = (workspaces || []).find(w => w.id === activeWorkspaceId);
    return ws?.type || null;
  }, [workspaces, activeWorkspaceId]);

  const key = activeWorkspaceId && wsType ? rosterKey(wsType, activeWorkspaceId) : null;

  // Seed synchronously from cache so a late-mounting consumer (e.g. an avatar
  // rendered after the first fetch resolved) needs no network round-trip at all.
  const [members, setMembers] = useState(() => (key && rosterCache.has(key) ? rosterCache.get(key) : []));
  const [loading, setLoading] = useState(() => !!key && !rosterCache.has(key));
  // Bumped by invalidateWorkspaceRoster() so this instance re-runs its load.
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const bump = () => setNonce(n => n + 1);
    rosterSubscribers.add(bump);
    return () => { rosterSubscribers.delete(bump); };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!key) {
      setMembers([]);
      setLoading(false);
      return;
    }

    if (rosterCache.has(key)) {
      setMembers(rosterCache.get(key));
      setLoading(false);
      return;
    }

    setLoading(true);
    loadRoster(key, wsType, currentUser).then(list => {
      if (!cancelled) {
        setMembers(list);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [key, wsType, currentUser?.id, nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  return { members, loading };
}

// Companion for leaf atoms (avatars, author chips): a memoised id → member Map
// built on the SAME cached members, so a lookup needs neither its own fetch nor
// a getUserById mock call. Resolves by member id and, where present, userId.
export function useWorkspaceRosterMap() {
  const { members } = useWorkspaceRoster();
  return useMemo(() => {
    const map = new Map();
    for (const m of members) {
      map.set(m.id, m);
      if (m.userId && m.userId !== m.id) map.set(m.userId, m);
    }
    return map;
  }, [members]);
}

// Pure @mention helpers that resolve against a roster's members instead of the
// global mock. Only account-holders (userId present) are mentionable, since a
// mention notifies by userId. Shapes mirror the old userManager equivalents so
// existing call sites (suggestion.handle, mention.userId) are unchanged.
export function rosterMentionSuggestions(members, query) {
  const q = (query || '').toLowerCase();
  return (members || [])
    .filter(m => m.userId)
    .filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
    .map(m => ({
      id: m.userId,
      name: m.name,
      email: m.email,
      handle: `@${(m.name || '').toLowerCase().replace(/\s+/g, '')}`,
      color: m.color,
      avatar: m.avatar,
    }));
}

export function rosterParseMentions(members, text) {
  if (!text || typeof text !== 'string') return [];
  const byName = new Map((members || []).filter(m => m.userId).map(m => [(m.name || '').toLowerCase(), m]));
  const re = /@([a-zA-Z0-9][a-zA-Z0-9\s]*[a-zA-Z0-9]|[a-zA-Z0-9])/g;
  const out = [];
  let match;
  while ((match = re.exec(text)) !== null) {
    const name = match[1].trim();
    const m = byName.get(name.toLowerCase());
    if (m) out.push({ mention: match[0], name, userId: m.userId, user: m, startIndex: match.index, endIndex: match.index + match[0].length });
  }
  return out;
}
